import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { preferenceEnums, BUDGET_RANGE } from "../../../system";
import { EMPTY_STRING, adminReduxConstants } from "../../../constants";
import { BackFromCustomerDetailIcon } from "../../../assets";
import {
  getLabelsByIds,
  boolToZeroOrOne,
  getLabelsByEnums,
  numberFormatting,
  formatSubscriptionDetails,
} from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions } from "../../../redux/actions";
import LabelValueDisplay from "./LabelValueDisplay";

const { GET_CUSTOMER_DETAILS } = adminReduxConstants;

const CustomerDetails = ({
  allHobbies,
  allCountries,
  allCurrencies,
  allPropertyTypes,
  allDayToDayActivity,
  customerDetails,
  spinnerActions,
  getCustomerDetails,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const getCurrencyCode = () => {
    const selectedCurrency = customerDetails?.defaultCurrencyId;
    return (
      allCurrencies?.find(
        (currency) => Number(selectedCurrency) === currency?.id
      )?.code || "USD"
    );
  };

  useEffect(() => {
    getCustomerDetails(id);
    // eslint-disable-next-line
  }, [id]);

  return (
    <MainContentWrapper className="position-relative">
      {spinner(GET_CUSTOMER_DETAILS) > -1 && (
        <MainSpinner className="admin-spinner" />
      )}
      {/* back arrow button */}
      <img
        src={BackFromCustomerDetailIcon}
        className="customer-detail-back-icon pointer"
        onClick={() => navigate("/admin/users/customers", { replace: true })}
        alt={EMPTY_STRING}
      />
      {/* customer details */}
      <div className="row">
        <div className="col-12 col-lg-8">
          <div className="admin-page-heading">Customers</div>
          <div className="heading-customer-name">{`${customerDetails?.name} ${customerDetails?.lastName}`}</div>
        </div>
        {/* Subscription Info Section */}
        <div className="col-12 col-lg-4 text-start text-lg-end">
          <div className="subscription-heading">Subscription</div>
          <div className="subscription-description">
            {formatSubscriptionDetails(customerDetails?.subscriptionPlan)}
          </div>
        </div>
      </div>

      <div className="data-container">
        <div className="row">
          <div className="col-12">
            <div className="info-heading">Personal Details</div>
          </div>
          {/* First name field */}
          <LabelValueDisplay label="First Name" value={customerDetails?.name} />
          {/* Last name field */}
          <LabelValueDisplay
            label="Last Name"
            value={customerDetails?.lastName}
          />
          {/* email field */}
          <LabelValueDisplay label="Email" value={customerDetails?.email} />
          {/* age group field */}
          <LabelValueDisplay
            label="Age"
            value={preferenceEnums.AGE_RANGE[customerDetails?.ageGroup]}
          />
          {/* preferences heading */}
          <div className="col-12">
            <div className="info-heading">Preferences</div>
          </div>
          {/* default currency */}
          <LabelValueDisplay
            label="Default Currency:"
            value={getLabelsByIds(
              allCurrencies,
              customerDetails?.defaultCurrencyId,
              "code"
            )}
          />
          {/* owns a home */}
          <LabelValueDisplay
            label="Owns a Home:"
            value={
              preferenceEnums.DECISION_CHOICES_BOOLEAN[
                boolToZeroOrOne(customerDetails?.ownsHouse)
              ]
            }
          />
          {/* family members */}
          <LabelValueDisplay
            label="Family Members:"
            value={getLabelsByEnums(
              preferenceEnums.FAMILY,
              customerDetails?.familyMemberIds
            )?.join(", ")}
          />
          <LabelValueDisplay
            label="Passports:"
            value={getLabelsByIds(
              allCountries,
              customerDetails?.passportHold,
              "name"
            )?.join(", ")}
          />
          <LabelValueDisplay
            label="Current Country of Residence:"
            value={getLabelsByIds(
              allCountries,
              customerDetails?.currentCountry,
              "name"
            )}
          />
          <LabelValueDisplay
            label="Places like to live:"
            value={getLabelsByIds(
              allPropertyTypes,
              customerDetails?.preferredLocationIds,
              "title"
            )?.join(", ")}
          />
          <LabelValueDisplay
            label="No. of properties like to own:"
            value={
              preferenceEnums.PROPERTIES_OWNS[
                customerDetails?.noPropertiesLikeToOwn
              ]
            }
          />
          <LabelValueDisplay
            label="Healthcare:"
            value={
              preferenceEnums.HEALTH_PRIORITY[customerDetails?.healthPriority]
            }
          />
          <LabelValueDisplay
            label="Day to day activities:"
            value={
              customerDetails?.dayToDay?.length > 0
                ? getLabelsByIds(
                    allDayToDayActivity,
                    customerDetails?.dayToDay,
                    "title"
                  )?.join(", ")
                : "N/A"
            }
          />
          <LabelValueDisplay
            label="Tax free country:"
            value={getLabelsByEnums(
              preferenceEnums.DECISION_CHOICES,
              customerDetails?.taxFreeCountry
            )}
          />
          <LabelValueDisplay
            label="Countries like to live:"
            value={getLabelsByIds(
              allCountries,
              customerDetails?.countriesLikeToLiveIds,
              "name"
            )?.join(", ")}
          />
          <LabelValueDisplay
            label="Visa free country:"
            value={getLabelsByEnums(
              preferenceEnums.DECISION_CHOICES,
              customerDetails?.visaFreeCountry
            )}
          />
          <LabelValueDisplay
            label="Citizenship:"
            value={
              customerDetails?.interestedCitizenshipId
                ? getLabelsByIds(
                    allCountries,
                    customerDetails?.interestedCitizenshipId,
                    "name"
                  )
                : "N/A"
            }
          />
          <LabelValueDisplay
            label="Flight time for travelling:"
            value={getLabelsByEnums(
              preferenceEnums.TRAVEL_DISTANCE_HOLIDAY,
              customerDetails?.travelDistanceHoliday
            )}
          />
          <LabelValueDisplay
            label="Proximity to loved ones:"
            value={getLabelsByEnums(
              preferenceEnums.TRAVEL_DISTANCE,
              customerDetails?.travelDistanceLovedOnes
            )}
          />
          <LabelValueDisplay
            label="Hobbies and Interests:"
            value={
              customerDetails?.hobbies?.length > 0
                ? getLabelsByIds(
                    allHobbies,
                    customerDetails?.hobbies,
                    "title"
                  )?.join(", ")
                : "N/A"
            }
          />
          <LabelValueDisplay
            label="Budget:"
            value={`${numberFormatting(customerDetails?.minBudget)} - ${
              customerDetails?.maxBudget === BUDGET_RANGE.MAX
                ? "1M+"
                : numberFormatting(customerDetails?.maxBudget)
            } ${getCurrencyCode()}`}
          />
        </div>
      </div>
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  allHobbies: state.homeReducer.allHobbies,
  allCurrencies: state.homeReducer.allCurrencies,
  allCountries: state.countryReducer.allCountries,
  allPropertyTypes: state.propertyReducer.allPropertyTypes,
  customerDetails: state.adminReducer.customerDetails,
  spinnerActions: state.systemReducer.spinnerActions,
  allDayToDayActivity: state.homeReducer.allDayToDayActivity,
});

const mapDispatchToProps = (dispatch) => ({
  getCustomerDetails: (id) => dispatch(adminActions.getCustomerDetails(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetails);
